import { CallsAndPuts, Chains, EnhancedChainData, Results } from '@benzinga/data-option-chain';

export const isValidUnderlyingSymbol = (underlyingSymbol: string) => {
  const regex = /^[A-Za-z]+$/;
  return regex.test(underlyingSymbol);
};

export function getBaseSymbol(symbol: string, charToRemove: string) {
  const lastIndex = symbol.lastIndexOf(charToRemove);
  if (lastIndex === -1) return symbol;
  return symbol.substring(0, lastIndex) + symbol.substring(lastIndex + 1);
}

export const getFormattedDataByExpiry = ({
  dataByExpiry,
  stockPrice,
  symbol,
}: {
  dataByExpiry: Record<string, Chains[]>;
  stockPrice: number;
  symbol: string;
}) => {
  const result = {} as {
    [key: string]: {
      calls: CallsAndPuts[];
      puts: CallsAndPuts[];
    };
  };

  Object.keys(dataByExpiry).forEach(expiry => {
    const { calls, puts, ...rest } = dataByExpiry[expiry]?.[0] || {};

    const newChainData: EnhancedChainData = {
      ...rest,
      calls: [],
      chains: [],
      puts: [],
      strike: 0,
    };

    const symbolMap = new Map();

    const callsAndPuts = [
      ['call', calls],
      ['put', puts],
    ] as Array<[string, CallsAndPuts[]]>;

    callsAndPuts.forEach(([type, data]) => {
      data.forEach(_data => {
        if (_data?.error || !isValidUnderlyingSymbol(symbol)) return;
        const inTheMoney = type === 'call' ? _data.strike < stockPrice : _data.strike > stockPrice;

        const data = { ..._data, inTheMoney };

        const baseSymbol = getBaseSymbol(data.symbol, type === 'call' ? 'C' : 'P');

        if (symbolMap.has(baseSymbol)) {
          symbolMap.set(baseSymbol, {
            ...symbolMap.get(baseSymbol),
            [type]: data,
          });
        } else {
          symbolMap.set(baseSymbol, {
            strike: data?.strike,
            [type]: data,
          });
        }
      });
    });

    newChainData.chains = Array.from(symbolMap.values()).sort((a, b) => a.strike - b.strike);

    result[expiry] = newChainData;
  });

  return result;
};

export const getStrikeRangeFromChain = (chain: EnhancedChainData) => {
  return [chain?.chains?.[0]?.strike || 0, chain?.chains?.slice(-1)?.[0]?.strike] as [number, number];
};

export const getfilteredData = ({
  data,
  moneyness,
  range,
  stockPrice,
}: {
  data: EnhancedChainData;
  range: [number, number];
  stockPrice: number;
  moneyness: string;
}) => {
  let result: Results[] = [];
  if (range) {
    result = data?.chains?.filter(
      chain => chain.strike >= range[0] && chain.strike <= range[1],
    ) as unknown as Results[];
  }

  if (moneyness === 'near-the-money') {
    const lowerBound = stockPrice * 0.95;
    const upperBound = stockPrice * 1.05;

    result = result?.filter((chain: Results) => chain.strike > lowerBound && chain.strike < upperBound);
  }

  if (moneyness === 'absolute-near-the-money') {
    const stockIndex = result?.findIndex(chain => chain.strike >= Math.round(stockPrice));

    if (stockIndex !== -1) {
      const start = Math.max(stockIndex - 5, 0);
      const end = Math.min(stockIndex + 5, result.length);
      result = result?.slice(start, end);
    }
  }

  return result;
};
